/**
 * Finanzas — Página top-level con sub-tabs:
 *   - Cierre de caja (totales del día por método de pago)
 *   - Cuentas por cobrar (pacientes con saldo pendiente)
 *
 * Acceso desde Dashboard → tarjeta "Finanzas".
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Tabs, Tab,
  TextField, Paper, Chip, Skeleton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';

const API_URL = '/api';
const userId = localStorage.getItem('user_id');

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#fff', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
};

const METODO_LABEL = {
  efectivo: 'Efectivo',
  tarjeta_debito: 'Débito',
  tarjeta_credito: 'Crédito',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  mercado_pago: 'Mercado Pago',
  stripe: 'Stripe',
  otro: 'Otro',
};

function fmtMoney(num, moneda = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(Number(num || 0));
}

function diasDesde(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
}

export default function Finanzas() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Header */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#64748b' }}>
            <ArrowBackIcon />
          </IconButton>
          <WalletIcon sx={{ fontSize: 32, color: '#15803d' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: { xs: 18, sm: 22 } }}>
              Finanzas
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              Cierre de caja, cuentas por cobrar y movimientos del consultorio
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, mb: 2 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', maxWidth: 1200, mx: 'auto' }}>
          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: '1px solid #e2e8f0',
              '& .MuiTab-root': {
                textTransform: 'none', fontSize: 13.5, fontWeight: 700, minHeight: 48,
                color: '#64748b',
                '&.Mui-selected': { color: '#15803d' },
              },
              '& .MuiTabs-indicator': { background: '#15803d', height: 3 },
            }}
          >
            <Tab icon={<MoneyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Cierre de caja" />
            <Tab icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Cuentas por cobrar" />
          </Tabs>
        </Paper>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, pb: 6, maxWidth: 1200, mx: 'auto' }}>
        {tab === 0 && <CierreCajaTab />}
        {tab === 1 && <CuentasPorCobrarTab />}
      </Box>
    </Box>
  );
}

// ============================================================================
// CIERRE DE CAJA
// ============================================================================
function CierreCajaTab() {
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [data, setData] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [resCierre, resPagos] = await Promise.all([
        fetch(`${API_URL}/emr/finanzas/cierre-caja?fecha=${fecha}`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/emr/finanzas/pagos?desde=${fecha}&hasta=${fecha}&limit=200`, { headers: { 'X-User-Id': userId } }),
      ]);
      if (resCierre.ok) setData(await resCierre.json());
      if (resPagos.ok) setPagos(await resPagos.json());
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      {/* Selector de fecha */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <CalendarIcon sx={{ color: '#15803d' }} />
        <TextField
          sx={inputStyle} size="small"
          type="date" label="Fecha"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          size="small" variant="outlined"
          onClick={() => setFecha(hoy)}
          sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, borderRadius: 2 }}
        >
          Hoy
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Recargar">
          <IconButton size="small" onClick={cargar} sx={{ color: '#64748b' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {cargando ? (
        <>
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3, mb: 2 }} />
          <Skeleton variant="rounded" height={250} sx={{ borderRadius: 3 }} />
        </>
      ) : (
        <>
          {/* Total del día — banner grande */}
          <Paper sx={{
            p: 4, mb: 3, borderRadius: 4,
            background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 12px 32px rgba(21,128,61,0.25)',
          }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Total del día · {new Date(fecha).toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}
              </Typography>
              <Typography sx={{ fontSize: 42, fontWeight: 800, lineHeight: 1.1, mt: 0.6 }}>
                {fmtMoney(data?.total_dia, data?.moneda)}
              </Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.9, mt: 0.4 }}>
                {data?.cantidad_pagos || 0} pago{(data?.cantidad_pagos || 0) !== 1 ? 's' : ''} registrado{(data?.cantidad_pagos || 0) !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <MoneyIcon sx={{ fontSize: 96, color: '#fef3c7', opacity: 0.65 }} />
          </Paper>

          {/* Grid: por método (izq) + lista de pagos (der) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr' }, gap: 2 }}>
            {/* Por método */}
            <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Desglose por método
                </Typography>
              </Box>
              {(data?.por_metodo || []).length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <MoneyIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>Sin movimientos</Typography>
                </Box>
              ) : (
                data.por_metodo.map((m, i) => {
                  const total = data.total_dia ? Number(data.total_dia) : 1;
                  const pct = (Number(m.total) / total) * 100;
                  return (
                    <Box key={m.metodo} sx={{
                      p: 1.5, position: 'relative',
                      borderBottom: i === data.por_metodo.length - 1 ? 'none' : '1px solid #f1f5f9',
                    }}>
                      <Box sx={{
                        position: 'absolute', top: 0, left: 0, bottom: 0,
                        width: `${pct}%`, background: '#dcfce7', zIndex: 0,
                      }} />
                      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                            {METODO_LABEL[m.metodo] || m.metodo}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                            {m.cantidad_pagos} pago{m.cantidad_pagos !== 1 ? 's' : ''} · {pct.toFixed(1)}%
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#15803d' }}>
                          {fmtMoney(m.total, data.moneda)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Paper>

            {/* Lista de pagos del día */}
            <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Pagos del día
                </Typography>
                <Chip label={pagos.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, background: '#15803d', color: '#fff' }} />
              </Box>
              {pagos.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>Sin pagos</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {pagos.map((p, i) => (
                    <Box key={p.id} sx={{
                      p: 1.5, display: 'flex', alignItems: 'center', gap: 1,
                      borderBottom: i === pagos.length - 1 ? 'none' : '1px solid #f1f5f9',
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>
                          {METODO_LABEL[p.metodo] || p.metodo}
                          {p.referencia && (
                            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b', ml: 0.8, fontWeight: 500 }}>
                              · {p.referencia}
                            </Box>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          {new Date(p.fecha_pago).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                          {p.notas && ` · ${p.notas.slice(0, 40)}`}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#15803d' }}>
                        {fmtMoney(p.monto, p.moneda)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
}

// ============================================================================
// CUENTAS POR COBRAR
// ============================================================================
function CuentasPorCobrarTab() {
  const navigate = useNavigate();
  const [cargos, setCargos] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    fetch(`${API_URL}/emr/finanzas/cargos?estado=pendiente&limit=200`, { headers: { 'X-User-Id': userId } })
      .then(async (r1) => {
        const pendientes = r1.ok ? await r1.json() : [];
        const r2 = await fetch(`${API_URL}/emr/finanzas/cargos?estado=parcial&limit=200`, { headers: { 'X-User-Id': userId } });
        const parciales = r2.ok ? await r2.json() : [];
        return [...pendientes, ...parciales];
      })
      .then(async (todos) => {
        // Enriquecer con nombre del paciente
        const idsUnicos = [...new Set(todos.map(c => c.paciente_id))];
        const nombres = {};
        await Promise.all(idsUnicos.map(async (id) => {
          try {
            const r = await fetch(`${API_URL}/emr/pacientes/${id}`, { headers: { 'X-User-Id': userId } });
            if (r.ok) {
              const p = await r.json();
              nombres[id] = p.nombre_completo;
            }
          } catch {}
        }));
        return todos.map(c => ({ ...c, paciente_nombre: nombres[c.paciente_id] || '?' }));
      })
      .then((arr) => {
        // Ordenar por fecha más antigua primero (más urgente)
        arr.sort((a, b) => new Date(a.fecha_emision) - new Date(b.fecha_emision));
        setCargos(arr);
      })
      .catch(console.error)
      .finally(() => setCargando(false));
  }, []);

  // Agrupar por edad de saldo (aging)
  const buckets = {
    actual:  { label: '0-30 días',   color: '#15803d', bg: '#dcfce7', cargos: [] },
    medio:   { label: '31-60 días',  color: '#b45309', bg: '#fef3c7', cargos: [] },
    viejo:   { label: '61-90 días',  color: '#c2410c', bg: '#fed7aa', cargos: [] },
    critico: { label: '> 90 días',   color: '#991b1b', bg: '#fee2e2', cargos: [] },
  };
  for (const c of cargos) {
    const d = diasDesde(c.fecha_emision);
    if (d <= 30) buckets.actual.cargos.push(c);
    else if (d <= 60) buckets.medio.cargos.push(c);
    else if (d <= 90) buckets.viejo.cargos.push(c);
    else buckets.critico.cargos.push(c);
  }

  const totalAR = cargos.reduce((s, c) => s + Number(c.saldo), 0);

  return (
    <Box>
      {/* Banner total AR */}
      <Paper sx={{
        p: 3, mb: 3, borderRadius: 4,
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 12px 32px rgba(220,38,38,0.20)',
      }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Saldo total por cobrar
          </Typography>
          <Typography sx={{ fontSize: 36, fontWeight: 800, lineHeight: 1.1, mt: 0.4 }}>
            {fmtMoney(totalAR)}
          </Typography>
          <Typography sx={{ fontSize: 12.5, opacity: 0.9, mt: 0.4 }}>
            {cargos.length} cargo{cargos.length !== 1 ? 's' : ''} pendientes en {new Set(cargos.map(c => c.paciente_id)).size} paciente{new Set(cargos.map(c => c.paciente_id)).size !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TrendingUpIcon sx={{ fontSize: 96, color: '#fef3c7', opacity: 0.55 }} />
      </Paper>

      {/* Aging buckets */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        {Object.entries(buckets).map(([k, b]) => {
          const total = b.cargos.reduce((s, c) => s + Number(c.saldo), 0);
          return (
            <Paper key={k} sx={{
              p: 1.5, borderRadius: 3, border: `1px solid ${b.color}33`,
              borderLeft: `4px solid ${b.color}`, background: b.bg,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: b.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {b.label}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: b.color, lineHeight: 1.1, mt: 0.3 }}>
                {fmtMoney(total)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: b.color, opacity: 0.85, mt: 0.2 }}>
                {b.cargos.length} cargo{b.cargos.length !== 1 ? 's' : ''}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Lista de cargos pendientes */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Cargos pendientes (ordenados por antigüedad)
          </Typography>
        </Box>
        {cargando ? (
          <Skeleton variant="rounded" height={300} />
        ) : cargos.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>
              Sin cuentas por cobrar
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
              Todos los cargos están pagados
            </Typography>
          </Box>
        ) : (
          cargos.map((c, i) => {
            const d = diasDesde(c.fecha_emision);
            const colorEdad = d > 90 ? '#991b1b' : d > 60 ? '#c2410c' : d > 30 ? '#b45309' : '#15803d';
            return (
              <Box key={c.id} sx={{
                p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                borderBottom: i === cargos.length - 1 ? 'none' : '1px solid #f1f5f9',
                cursor: 'pointer',
                '&:hover': { background: '#fafbfc' },
              }}
                onClick={() => navigate(`/emr?paciente=${c.paciente_id}&tab=cuenta`)}
              >
                <Box sx={{
                  width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                  background: '#f1f5f9', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800,
                }}>
                  {(c.paciente_nombre || '?').charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b' }}>
                    {c.paciente_nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                    {c.descripcion} · {new Date(c.fecha_emision).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                    {c.estado === 'parcial' && ` · pago parcial ${fmtMoney(c.pagado, c.moneda)}/${fmtMoney(c.total, c.moneda)}`}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#dc2626' }}>
                    {fmtMoney(c.saldo, c.moneda)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: colorEdad, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {d} día{d !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Paper>
    </Box>
  );
}
